<template>
  <v-dialog v-model="internalValue" max-width="1200px" scrollable>
    <v-card class="rounded-xl border-light bg-white">
      <v-card-title class="d-flex align-center justify-space-between py-3 px-5 border-light-bottom">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-file-document-outline</v-icon>
          <span class="text-h6 font-weight-black grey--text text--darken-4">
            Detalle del Contrato Comercial: {{ contract.EccContractNumber }}
          </span>
          <v-chip small :color="getStatusColor(contract.EccStatus)" class="font-weight-bold ml-2" text-color="white">
            {{ contract.StatusName || getStatusName(contract.EccStatus) }}
          </v-chip>
        </div>
        <v-btn icon color="grey darken-2" small @click="closeModal">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-4 pb-4 px-5 bg-white">
        <!-- 1. DATOS DEL CONTRATO -->
        <div class="text-subtitle-2 font-weight-bold grey--text text--darken-4 text-uppercase mb-2">
          <v-icon small color="grey darken-3" class="mr-1">mdi-clipboard-text-outline</v-icon>
          1. Datos del Contrato
        </div>

        <v-card outlined class="pa-4 rounded-xl border-light mb-4 bg-grey-lighten-5">
          <v-row dense class="text-caption">
            <v-col cols="12" sm="3">
              <span class="grey--text text--darken-2">N° Contrato / PA:</span>
              <div class="font-weight-bold grey--text text--darken-4 text-body-2">{{ contract.EccContractNumber }}</div>
            </v-col>
            <v-col cols="12" sm="3">
              <span class="grey--text text--darken-2">Destino / División:</span>
              <div class="font-weight-bold grey--text text--darken-4 text-body-2">{{ contract.TypeDestinationName || (contract.TypeDestination === 1 ? 'Frescos' : 'IQF / Congelados') }}</div>
            </v-col>
            <v-col cols="12" sm="3">
              <span class="grey--text text--darken-2">Campaña:</span>
              <div class="font-weight-bold grey--text text--darken-4 text-body-2">{{ contract.CmpName || contract.CmpID || '-' }}</div>
            </v-col>
            <v-col cols="12" sm="3">
              <span class="grey--text text--darken-2">Fecha del Acuerdo:</span>
              <div class="font-weight-bold grey--text text--darken-4 text-body-2">{{ $fun.formatDateView(contract.EccAgreementDate) }}</div>
            </v-col>

            <v-col cols="12" sm="6" class="mt-2">
              <span class="grey--text text--darken-2">Cliente Comercial:</span>
              <div class="font-weight-bold grey--text text--darken-4 text-body-2">{{ contract.EccCustomerName }} ({{ contract.PrsCustomerID }})</div>
            </v-col>
            <v-col cols="12" sm="6" class="mt-2">
              <span class="grey--text text--darken-2">Comprador Legal:</span>
              <div class="font-weight-bold grey--text text--darken-4 text-body-2">{{ contract.EccBuyerLegalName || '-' }}</div>
            </v-col>

            <v-col cols="12" sm="6" class="mt-2">
              <span class="grey--text text--darken-2">Proveedor:</span>
              <div class="font-weight-bold grey--text text--darken-4 text-body-2">{{ contract.EccProviderName }}</div>
            </v-col>
            <v-col cols="12" sm="3" class="mt-2">
              <span class="grey--text text--darken-2">Inicio Vigencia:</span>
              <div class="font-weight-bold grey--text text--darken-4 text-body-2">{{ contract.EccValidityStartDate ? $fun.formatDateView(contract.EccValidityStartDate) : '-' }}</div>
            </v-col>
            <v-col cols="12" sm="3" class="mt-2">
              <span class="grey--text text--darken-2">Fin Vigencia:</span>
              <div class="font-weight-bold grey--text text--darken-4 text-body-2">{{ contract.EccValidityEndDate ? $fun.formatDateView(contract.EccValidityEndDate) : '-' }}</div>
            </v-col>

            <v-col cols="12" sm="3" class="mt-2">
              <span class="grey--text text--darken-2">Moneda:</span>
              <div class="font-weight-bold grey--text text--darken-4 text-body-2">{{ contract.TypeCurrencyName || 'USD' }}</div>
            </v-col>
            <v-col cols="12" sm="3" class="mt-2">
              <span class="grey--text text--darken-2">Incoterm / Lugar:</span>
              <div class="font-weight-bold grey--text text--darken-4 text-body-2">{{ contract.TypeIncotermName || 'FOB' }} {{ contract.EccIncotermPlace ? `(${contract.EccIncotermPlace})` : '' }}</div>
            </v-col>
            <v-col cols="12" sm="3" class="mt-2">
              <span class="grey--text text--darken-2">Condición de Pago:</span>
              <div class="font-weight-bold grey--text text--darken-4 text-body-2">{{ contract.EccPaymentCondition }}</div>
            </v-col>
            <v-col cols="12" sm="3" class="mt-2">
              <span class="grey--text text--darken-2">Ejecutivo Comercial:</span>
              <div class="font-weight-bold grey--text text--darken-4 text-body-2">{{ contract.EccCommercialExecutive || '-' }}</div>
            </v-col>

            <v-col cols="12" class="mt-2" v-if="contract.EccObservation">
              <span class="grey--text text--darken-2">Observaciones:</span>
              <div class="grey--text text--darken-4">{{ contract.EccObservation }}</div>
            </v-col>
          </v-row>
        </v-card>

        <!-- 2. PRODUCTOS CONTRATADOS -->
        <div class="text-subtitle-2 font-weight-bold grey--text text--darken-4 text-uppercase mb-2">
          <v-icon small color="grey darken-3" class="mr-1">mdi-package-variant</v-icon>
          2. Productos Contratados ({{ contract.Items ? contract.Items.length : 0 }})
        </div>

        <v-simple-table dense class="border-rounded border-light mb-4">
          <template v-slot:default>
            <thead>
              <tr class="grey lighten-4">
                <th class="text-left font-weight-bold grey--text text--darken-4">Producto / Descripción</th>
                <th class="text-left font-weight-bold grey--text text--darken-4">Presentación</th>
                <th class="text-center font-weight-bold grey--text text--darken-4">Cantidad</th>
                <th class="text-center font-weight-bold grey--text text--darken-4">KG Declarados</th>
                <th class="text-center font-weight-bold grey--text text--darken-4">KG Calculados</th>
                <th class="text-center font-weight-bold grey--text text--darken-4">Diferencia</th>
                <th class="text-center font-weight-bold grey--text text--darken-4">Precio Unit.</th>
                <th class="text-right font-weight-bold grey--text text--darken-4">Importe</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in contract.Items" :key="idx">
                <td>
                  <div class="font-weight-bold text-caption grey--text text--darken-4">{{ item.EciProductName }}</div>
                  <div class="caption grey--text text--darken-2" v-if="item.EciContractDescription && item.EciContractDescription !== item.EciProductName">
                    {{ item.EciContractDescription }}
                  </div>
                </td>
                <td class="caption">{{ item.EciPresentation }}</td>
                <td class="text-center font-weight-bold caption">{{ item.EciContractedQuantity | formatNum }} {{ item.TypeQuantityUnitName || 'Cajas' }}</td>
                <td class="text-center caption">{{ item.EciDeclaredKg | formatNum }} kg</td>
                <td class="text-center caption">{{ item.EciCalculatedKg | formatNum }} kg</td>
                <td class="text-center">
                  <span v-if="Math.abs(item.EciKgDifference) <= 1.0" class="caption grey--text text--darken-2 font-weight-bold">
                    {{ item.EciKgDifference | formatNum }} kg
                  </span>
                  <v-chip v-else x-small color="warning" text-color="white" class="font-weight-bold">
                    {{ item.EciKgDifference | formatNum }} kg
                  </v-chip>
                </td>
                <td class="text-center font-weight-bold caption">{{ item.EciUnitPrice | formatCurrency }} {{ item.TypePriceUnitName || 'USD/kg' }}</td>
                <td class="text-right font-weight-bold primary--text text--darken-2 caption">{{ item.EciContractAmount | formatCurrency }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <!-- 3. DOCUMENTO PDF -->
        <div class="text-subtitle-2 font-weight-bold grey--text text--darken-4 text-uppercase mb-2">
          <v-icon small color="grey darken-3" class="mr-1">mdi-file-pdf-box</v-icon>
          3. Documento Contractual Adjunto
        </div>

        <div v-if="contract.EccFileName" class="d-flex align-center">
          <v-btn color="primary" class="rounded-lg text-none font-weight-bold mr-2" small @click="previewPdf">
            <v-icon left small>mdi-eye</v-icon>
            Visualizar PDF
          </v-btn>
          <v-btn color="grey darken-3" outlined class="rounded-lg text-none font-weight-bold" small @click="downloadPdf">
            <v-icon left small>mdi-download</v-icon>
            Descargar PDF ({{ contract.EccFileOriginalName || contract.EccFileName }})
          </v-btn>
        </div>
        <div v-else class="caption grey--text">
          No hay archivo PDF adjunto a este contrato.
        </div>
      </v-card-text>

      <v-card-actions class="px-5 pb-4 pt-2 border-light-top bg-white">
        <v-spacer />
        <v-btn text class="rounded-lg px-4 text-none font-weight-bold" @click="closeModal">
          Cerrar
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Modal Visor PDF -->
    <v-dialog v-model="pdfDialog.show" max-width="900px" scrollable>
      <v-card class="rounded-xl">
        <v-card-title class="d-flex align-center justify-space-between py-3 px-4 border-light-bottom">
          <span class="text-subtitle-1 font-weight-bold grey--text text--darken-4">
            {{ contract.EccFileOriginalName || 'Contrato Comercial' }}
          </span>
          <v-btn icon small @click="pdfDialog.show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0" style="height: 600px;">
          <iframe
            v-if="pdfDialog.url"
            :src="pdfDialog.url"
            width="100%"
            height="100%"
            style="border: none;"
          ></iframe>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import _sCommercialContract from "@/services/ExternalTrade/CommercialContractService.js";
import _GenUploadFiles from "@/services/General/GenUploadFiles.js";

export default {
  name: "CommercialContractDetailModal",
  filters: {
    formatNum(val) {
      if (val === null || val === undefined || isNaN(val)) return "0";
      return new Intl.NumberFormat("es-PE", { maximumFractionDigits: 2 }).format(val);
    },
    formatCurrency(val) {
      if (val === null || val === undefined || isNaN(val)) return "0.00";
      return new Intl.NumberFormat("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val);
    },
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    contractId: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      contract: {},
      pdfDialog: {
        show: false,
        url: null,
      },
    };
  },
  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
  watch: {
    value(val) {
      if (val && this.contractId) {
        this.loadContract(this.contractId);
      }
    },
  },
  methods: {
    getStatusName(status) {
      const map = { 1: "Borrador", 2: "Registrado", 3: "Anulado", 4: "Cerrado" };
      return map[status] || "Desconocido";
    },
    getStatusColor(status) {
      const map = { 1: "grey darken-1", 2: "primary", 3: "error", 4: "teal" };
      return map[status] || "grey";
    },
    async loadContract(id) {
      try {
        const resp = await _sCommercialContract.getById(id, this.$fun.getUserID());
        if (resp && resp.status === 200 && resp.data) {
          this.contract = resp.data;
        }
      } catch (err) {
        console.error("Error al cargar detalle del contrato:", err);
        this.$fun.alert("Error al obtener detalle del contrato comercial.", "error");
      }
    },
    async previewPdf() {
      if (!this.contract.EccFileName) return;
      try {
        const fileObj = await _GenUploadFiles.getFileForPreview("RutaAnexosSAP", this.contract.EccFileName);
        if (fileObj && fileObj.url) {
          this.pdfDialog.url = fileObj.url;
          this.pdfDialog.show = true;
        }
      } catch (err) {
        console.error("Error al previsualizar PDF:", err);
        this.$fun.alert("No se pudo previsualizar el archivo PDF.", "error");
      }
    },
    downloadPdf() {
      if (!this.contract.EccFileName) return;
      _GenUploadFiles.getFile("RutaAnexosSAP", this.contract.EccFileName);
    },
    closeModal() {
      this.internalValue = false;
    },
  },
};
</script>

<style scoped>
.border-light {
  border: 1px solid #e2e8f0 !important;
}

.border-light-bottom {
  border-bottom: 1px solid #e2e8f0 !important;
}

.border-light-top {
  border-top: 1px solid #e2e8f0 !important;
}

.bg-grey-lighten-5 {
  background-color: #f8fafc !important;
}

.border-rounded {
  border-radius: 8px;
  overflow: hidden;
}
</style>
